import { cellToBoundary, cellToLatLng } from "h3-js"
import { BoundingBox } from "../types"
import {
    aggregateToH3,
    fetchActiveFirePoints,
    h3ResForZoom,
    heatFillColorForCount,
    heatOpacityForResolution,
    HEAT_HEX_COVERAGE,
} from "./useHeatLayer"

function shrinkHexRingTowardCentroid(
    ring: [number, number][],
    coverage: number
): [number, number][] {
    const n = ring.length
    const cx = ring.reduce((sum, [x]) => sum + x, 0) / n
    const cy = ring.reduce((sum, [, y]) => sum + y, 0) / n
    return ring.map(([x, y]) => [cx + (x - cx) * coverage, cy + (y - cy) * coverage])
}

export async function getHeatGeoJsonForRegion(bbox: BoundingBox, zoom: number) {
    const points = await fetchActiveFirePoints()
    const h3Res = h3ResForZoom(zoom)

    // scope to all fires, not the print region — matches useHeatLayer.ts's color scale
    const allCells = aggregateToH3(points, h3Res)
    const maxCount = allCells.reduce((m, d) => Math.max(m, d.count), 1)

    const cellsInRegion = allCells.filter(({ hex }) => {
        const [lat, lon] = cellToLatLng(hex)
        return lat >= bbox.south && lat <= bbox.north && lon >= bbox.west && lon <= bbox.east
    })

    const features = cellsInRegion.map(({ hex, count }) => {
        const [r, g, b, a] = heatFillColorForCount(count, maxCount)
        return {
            type: "Feature" as const,
            geometry: {
                type: "Polygon" as const,
                coordinates: [
                    shrinkHexRingTowardCentroid(cellToBoundary(hex, true), HEAT_HEX_COVERAGE),
                ],
            },
            properties: { count, r, g, b, a },
        }
    })

    return {
        geoJson: {
            type: "FeatureCollection" as const,
            features,
        },
        opacity: heatOpacityForResolution(h3Res),
    }
}
